import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "./index";
import type { LandingCarouselFace } from "../domain/CmsSlice/LandingCarouselFace";
import { hashPicked } from "../application/Common/dataHasher";

export interface CarouselCachedItem {
  landingCarouselFaceId: number;
  item: LandingCarouselFace;
  hash: string; // hash of important fields
}

export interface CarouselCacheState {
  items: Record<number, CarouselCachedItem>;
  order: number[]; // stable order for rendering
  rehydrateTimestamp: number | null; // epoch ms when we can fetch again
}

const initialState: CarouselCacheState = {
  items: {},
  order: [],
  rehydrateTimestamp: null,
};

// compute hash for a face using only relevant fields
function computeFaceHash(face: LandingCarouselFace): string {
  return hashPicked(face, [
    "landingCarouselFaceId",
    "imageUrl",
    "title",
    "contentText",
    "navigateUrl",
    "isExternalNavigateUrl",
    "callToAction",
  ] as (keyof LandingCarouselFace)[]);
}

const carouselCacheSlice = createSlice({
  name: "carouselCache",
  initialState,
  reducers: {
    hydrateFromApi(state, a: PayloadAction<LandingCarouselFace[]>) {
      const REHYDRATE_TTL_MS = 5 * 60 * 1000; // 5 minutes
      const incoming = a.payload;
      const incomingIds = new Set<number>();

      // upsert based on hash
      for (const face of incoming) {
        const id = face.landingCarouselFaceId;
        incomingIds.add(id);
        const hash = computeFaceHash(face);
        const existing = state.items[id];
        if (!existing || existing.hash !== hash) {
          state.items[id] = { landingCarouselFaceId: id, item: face, hash };
        }
      }

      // delete removed
      for (const id of Object.keys(state.items).map((s) => Number(s))) {
        if (!incomingIds.has(id)) {
          delete state.items[id];
        }
      }

      // update order following incoming order
      state.order = incoming.map((f) => f.landingCarouselFaceId);

      // schedule next allowed hydration in 5 minutes
      state.rehydrateTimestamp = Date.now() + REHYDRATE_TTL_MS;
    },
    clear(state) {
      state.items = {};
      state.order = [];
      state.rehydrateTimestamp = null;
    },
  },
});

export const { hydrateFromApi, clear } = carouselCacheSlice.actions;
export default carouselCacheSlice.reducer;

// selectors
export const selectCarouselCachedFaces = (
  s: RootState
): LandingCarouselFace[] => {
  const cache = (s as any).carouselCache as CarouselCacheState | undefined;
  if (!cache?.order?.length) return [];
  return cache.order
    .map((id: number) => cache.items[id]?.item)
    .filter(Boolean) as LandingCarouselFace[];
};

export const selectCarouselRehydrateTimestamp = (
  s: RootState
): number | null => {
  const cache = (s as any).carouselCache as CarouselCacheState | undefined;
  return cache?.rehydrateTimestamp ?? null;
};

// facade
export function useCarouselCacheStore() {
  const dispatch = useDispatch<AppDispatch>();
  const faces = useSelector(selectCarouselCachedFaces);
  const rehydrateTimestamp = useSelector(selectCarouselRehydrateTimestamp);
  const doHydrate = useCallback(
    (incoming: LandingCarouselFace[]) => dispatch(hydrateFromApi(incoming)),
    [dispatch]
  );
  const doClear = useCallback(() => dispatch(clear()), [dispatch]);
  return {
    faces,
    rehydrateTimestamp,
    hydrate: doHydrate,
    clear: doClear,
  } as const;
}
